import React, {useEffect, useState} from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Switch,
  Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {useTranslation} from "react-i18next";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {PrimarySmallButton} from "../../components/buttons/mainButton";
import {Cookies} from "react-cookie-consent";

interface CookieDialogProps {
    open: boolean;
    close: () => void;
    handleAcceptCookie: (stripe: boolean, analysis: boolean, ga?: boolean, gtm?: boolean) => void;
}

export default function CookieDialog(props: CookieDialogProps): JSX.Element {
  const {t} = useTranslation();
  const {open, close, handleAcceptCookie} = props;

  const [analysisCookie, setAnalysisCookie] = useState<boolean>(true);
  const [gaCookie, setGaCookie] = useState<boolean>(true);
  const [gtmCookie, setGtmCookie] = useState<boolean>(true);
  const [stripeCookie, setStripeCookie] = useState<boolean>(true);

  useEffect(() => {
    let isConsent = Cookies.get('joozyCookieConsent');  
        
    if (isConsent !== undefined) {
      isConsent = JSON.parse(isConsent);
      setGaCookie(isConsent['ga']);
      setGtmCookie(isConsent['gtm']);
      setStripeCookie(isConsent['stripe']);
    }
  }, [])

  useEffect(() => {
    if (!gaCookie || !gtmCookie) {
      setAnalysisCookie(false);
    } else {
      setAnalysisCookie(true);
    }
  }, [gaCookie, gtmCookie])

  const handleAnalysisCookie = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean): void => {
    setAnalysisCookie(!analysisCookie);
    if (!checked) {
      setGaCookie(false);
      setGtmCookie(false);
    } else {
      setGaCookie(true);
      setGtmCookie(true);
    }
  }

  return (
    <Dialog open={open} onClose={close} scroll="paper" sx={{display: "flex", justifyContent: "center", 
      ".MuiDialog-container": {
        width: "100%", 
        ".MuiPaper-root": {width: "100%", maxWidth: "650px", minWidth: "350px", position: "relative", borderRadius: "25px"}
      }
    }} maxWidth="lg">
      <CloseIcon onClick={close} sx={{color: "neutral.main", height: "32px", position: "absolute", top: "8px", right: "8px", cursor: "pointer", padding: "10px"}}/>
      <DialogTitle sx={{display: "flex", alignItems: "start", justifyContent: "center", flexDirection: "column"}}>
        <Typography variant="body1" color="neutral.dark" sx={{fontWeight: "bold", fontSize: "24px", mt: "4px", "span": {color: "landing.main"}}} dangerouslySetInnerHTML={{__html: t("cookie.popup.title")}} />
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" color="neutral.dark" sx={{fontWeight: "400", fontSize: "16px", width: "100%", mt: 1, textAlign: "justify"}}>
          {t("cookie.popup.body")}
        </Typography>
        <Box sx={{mt: 4}}>
          <Box>
            <Typography variant="h2" color="neutral.dark" sx={{fontWeight: "bold", fontSize: "16px", mt: "4px", "span": {color: "landing.main"}}} dangerouslySetInnerHTML={{__html: t("cookie.popup.essentials")}} />
            <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
              <Typography onClick={(): void => setStripeCookie(!stripeCookie)} variant="body1" color="neutral.dark" sx={{fontWeight: "400", fontSize: "16px", mt: 1, textAlign: "start", cursor: "pointer"}}>
                {t("cookie.popup.essentials_body")}
              </Typography>
              <Switch
                sx={{ml: 3}}
                checked={stripeCookie}
                onChange={(): void => setStripeCookie(!stripeCookie)}
                inputProps={{'aria-label': 'controlled'}}
              />
            </Box>
            <Accordion sx={{
              mt: "0px !important", 
              boxShadow: "none", 
              borderRadius: "0px !important", 
              minWidth: "unset !important",
              maxWidth: "unset !important",
              height: 'fit-content', 
              "&::before": {display: "none"}, 
              ".MuiAccordionSummary-root": {padding: "0px", minHeight: "unset !important", 
                ".MuiAccordionSummary-content": {margin: "0px"}},
              ".MuiCollapse-vertical": {".MuiAccordionDetails-root" : {padding: "0px !important"}}
            }}>
              <AccordionSummary id="panel-header" aria-controls="panel-content" sx={{width: "fit-content"}}>
                <Box sx={{width: "fit-content", display: "flex", alignItems: "center", cursor: "pointer", mt: "12px", borderBottomWidth: "1px", borderBottomColor: "primary.main", borderBottomStyle: "solid"}}>
                  <Typography variant="body2" sx={{mr: "4px", fontWeight: 600}} color="primary.main">{t("cookie.popup.more")}</Typography>
                  <KeyboardArrowDownIcon fontSize="small" sx={{color: "primary.main", fontWeight: 600}} />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", py: 1, mt: 1}}>
                  <Box>
                    <Typography variant="body1" color="neutral.dark" sx={{fontWeight: "bold", fontSize: "14px", textAlign: "start"}}>
                      {t("cookie.popup.stripe")}
                    </Typography>
                    <Typography variant="body1" color="neutral.dark" sx={{fontWeight: "400", fontSize: "14px", mr: 1, textAlign: "start"}}>
                      {t("cookie.popup.stripe_body")}
                    </Typography>
                  </Box>
                  <Switch
                    sx={{ml: 3}}
                    checked={stripeCookie}
                    onChange={(): void => setStripeCookie(!stripeCookie)}
                    inputProps={{'aria-label': 'controlled'}}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
          <Box sx={{mt: 4}}>
            <Typography variant="h2" color="neutral.dark" sx={{fontWeight: "bold", fontSize: "16px", mt: "4px", "span": {color: "landing.main"}}} dangerouslySetInnerHTML={{__html: t("cookie.popup.analysis")}} />
            <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
              <Typography variant="body1" color="neutral.dark" sx={{fontWeight: "400", fontSize: "16px", mt: 1, mr: 1, textAlign: "start"}}>
                {t("cookie.popup.analysis_body")}
              </Typography>
              <Switch
                sx={{ml: 3}}
                checked={analysisCookie}
                onChange={handleAnalysisCookie}
                inputProps={{'aria-label': 'controlled'}}
              />
            </Box>
            <Accordion sx={{
              mt: "0px !important", 
              boxShadow: "none", 
              borderRadius: "0px !important", 
              minWidth: "unset !important",
              maxWidth: "unset !important",
              height: 'fit-content', 
              "&::before": {display: "none"}, 
              ".MuiAccordionSummary-root": {padding: "0px", minHeight: "unset !important", 
                ".MuiAccordionSummary-content": {margin: "0px"}},
              ".MuiCollapse-vertical": {".MuiAccordionDetails-root" : {padding: "0px !important"}}
            }}>
              <AccordionSummary id="panel-header" aria-controls="panel-content" sx={{width: "fit-content"}}>
                <Box sx={{width: "fit-content", display: "flex", alignItems: "center", cursor: "pointer", mt: "12px", borderBottomWidth: "1px", borderBottomColor: "primary.main", borderBottomStyle: "solid"}}>
                  <Typography variant="body2" sx={{mr: "4px", fontWeight: 600}} color="primary.main">{t("cookie.popup.more")}</Typography>
                  <KeyboardArrowDownIcon fontSize="small" sx={{color: "primary.main", fontWeight: 600}} />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", py: 1, mt: 1}}>
                  <Box>
                    <Typography variant="body1" color="neutral.dark" sx={{fontWeight: "bold", fontSize: "14px", textAlign: "start"}}>
                      {t("cookie.popup.gtm")}
                    </Typography>
                    <Typography variant="body1" color="neutral.dark" sx={{fontWeight: "400", fontSize: "14px", mr: 1, textAlign: "start"}}>
                      {t("cookie.popup.gtm_body")}
                    </Typography>
                  </Box>
                  <Switch
                    sx={{ml: 3}}
                    checked={gaCookie}
                    onChange={(): void => setGaCookie(!gaCookie)}
                    inputProps={{'aria-label': 'controlled'}}
                  />
                </Box>
                <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", py: 1}}>
                  <Box>
                    <Typography variant="body1" color="neutral.dark" sx={{fontWeight: "bold", fontSize: "14px", textAlign: "start"}}>
                      {t("cookie.popup.ga")}
                    </Typography>
                    <Typography variant="body1" color="neutral.dark" sx={{fontWeight: "400", fontSize: "14px", mr: 1, textAlign: "start"}}>
                      {t("cookie.popup.ga_body")}
                    </Typography>
                  </Box>
                  <Switch
                    sx={{ml: 3}}
                    checked={gtmCookie}
                    onChange={(): void => setGtmCookie(!gtmCookie)}
                    inputProps={{'aria-label': 'controlled'}}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        </Box>
        <Box sx={{width: "100%", textAlign: "end", mt: 5}}>
          <PrimarySmallButton label={t("cookie.popup.confirm")} action={(): void => handleAcceptCookie(stripeCookie, analysisCookie, gaCookie, gtmCookie)} />
        </Box>
      </DialogContent>
    </Dialog>
  )
}
